import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { styled, withStyles } from '@material-ui/core/styles'
import { rgba } from 'polished'
import { Paper, Divider, Box as MuiBox, Typography } from '@material-ui/core'
import Slider from '@material-ui/core/Slider'
import { Translate } from 'react-localize-redux'
import { InnrHead } from 'containers'
import { fetchSkills, updateSkillValue } from 'api/players/api'
import useTeamFeature from 'hooks/useTeamFeature'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const Box = styled(MuiBox)(({ theme }) => ({
  padding: theme.spacing(0, 2, 2),
  overflow: 'auto',
  '& .MuiBox-root': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& .MuiTypography-root': {
      width: 222,
    },
  },
}))

const PrettoSlider = withStyles((theme) => ({
  root: {
    color: rgba(theme.palette.primary.main, 1),
    height: 7,
    width: 'calc(100% - 173px)',
  },
  thumb: {
    height: 18,
    width: 18,
    backgroundColor: '#fff',
    border: '4px solid',
    borderColor: rgba(theme.palette.primary.main, 1),
    marginTop: -6,
    marginLeft: -2,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 4px)',
  },
  track: {
    color: rgba(theme.palette.primary.main, 1),
    height: 7,
    borderRadius: 4,
  },
  rail: {
    color: '#D0D0D0',
    height: 7,
    borderRadius: 8,
  },
}))(Slider)

const SkillRatingsWidget = ({ currentPlayer }) => {
  const { enabled: hasTeamSkillsEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.team_skills_enabled
  )
  const featureGuard = useFeatureAccessGuard()

  const [sliderSkills, setSliderSkills] = useState([])

  useEffect(() => {
    fetchSkills(currentPlayer.uuid)
      .then((res) => setSliderSkills(res.data))
      .catch((error) => console.error('Error fetching player skills:', error))
  }, [])

  // handle skill value change in local state while dragging
  const handleSkillValueChange = (uuid, value) => {
    if (featureGuard(hasTeamSkillsEnabled)) return

    setSliderSkills(
      sliderSkills.map((skill) =>
        skill.uuid === uuid ? { ...skill, value } : skill
      )
    )
  }

  // commit actual skill value change
  const handleSkillValueCommit = (uuid, value) => {
    if (featureGuard(hasTeamSkillsEnabled)) return

    updateSkillValue(uuid, value).catch((error) =>
      console.error('Error updating player skill:', error)
    )
  }

  return (
    <Paper className="h-100">
      <InnrHead
        title={
          <>
            <Translate id="team.player-skill-ratings" />
            {!hasTeamSkillsEnabled ? (
              <LockIconComponent context="label" />
            ) : null}
          </>
        }
        divider={<Divider />}
      />
      <Box>
        {sliderSkills.map((skill) => {
          return (
            <MuiBox key={skill.uuid}>
              <Typography noWrap c>
                {skill.name}
                {!hasTeamSkillsEnabled ? (
                  <LockIconComponent context="label" />
                ) : null}
              </Typography>
              <PrettoSlider
                max={10}
                step={1}
                value={skill.value}
                onChange={(e, value) =>
                  handleSkillValueChange(skill.uuid, value)
                }
                onChangeCommitted={(e, value) =>
                  handleSkillValueCommit(skill.uuid, value)
                }
              />
              <span>{skill.value}/10</span>
            </MuiBox>
          )
        })}
      </Box>
    </Paper>
  )
}

SkillRatingsWidget.propTypes = {
  currentPlayer: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
  }).isRequired,
}

export default SkillRatingsWidget
